import React, { useState, useEffect } from 'react';
import { TRYON_SERVER_URL } from '../config';
import { useSelector } from 'react-redux';
import { selectAccessToken } from '../selectors';
import { useNavigate } from 'react-router-dom';

const Feedback = () => {
  const [feedback, setFeedback] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const accessToken = useSelector(selectAccessToken);
  const navigate = useNavigate();

  useEffect(() => {
    const tokenCheck = setTimeout(() => {
      if (!accessToken) {
        navigate('/signin');
      }
      setIsLoading(false);
    }, 500);
    
    return () => clearTimeout(tokenCheck);
  }, [accessToken, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!feedback.trim()) {
      setError('Please enter your feedback before submitting');
      return;
    }

    setIsSubmitting(true);
    setError('');
    
    try {
      const response = await fetch(`${TRYON_SERVER_URL}/api/v1/user/feedback/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          feedback: feedback
        })
      });

      if (response.ok) {
        setSubmitSuccess(true);
        setFeedback('');
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to submit feedback. Please try again.');
      }
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setSubmitSuccess(false);
    setFeedback('');
    setError('');
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-[70vh]">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-500"></div>
          <p className="mt-4 text-gray-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-12">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">We Value Your Feedback</h1>
        <p className="text-gray-600 max-w-lg mx-auto">
          Help us improve our AI fashion platform by sharing your thoughts, suggestions, or reporting any issues you've encountered.
        </p>
      </div>

      {submitSuccess ? (
        <div className="bg-gradient-to-r from-rose-50 to-amber-50 rounded-xl shadow-md border border-rose-100 p-8 text-center">
          <div className="w-16 h-16 bg-white rounded-full mx-auto flex items-center justify-center mb-4 shadow-sm border border-rose-100">
            <svg className="w-8 h-8 text-rose-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h2>
          <p className="text-gray-600 mb-6">
            Your feedback has been submitted successfully. We appreciate your input and will use it to enhance our platform.
          </p>
          <button
            onClick={handleReset}
            className="px-6 py-2.5 bg-gradient-to-r from-rose-500 to-rose-400 hover:from-rose-600 hover:to-rose-500 text-white rounded-lg shadow-md shadow-rose-500/20 transition-colors font-medium"
          >
            Submit Another Feedback
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-lg backdrop-blur-[30px] bg-white/80 border border-rose-100/50 p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="feedback" className="block text-sm font-medium text-gray-700 mb-1">
                Your Feedback
              </label>
              <textarea
                id="feedback"
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder="Please share your experience, suggestions or report any issues you've encountered..."
                className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 outline-none resize-none min-h-[200px] text-base text-gray-900"
              />
              {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
            </div>

            <div className="flex items-center justify-end space-x-4">
              <button
                type="button"
                onClick={() => setFeedback('')}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-800 border border-gray-200 rounded-lg hover:bg-rose-50 hover:border-rose-200 transition-colors"
                disabled={isSubmitting || !feedback.trim()}
              >
                Clear
              </button>
              <button
                type="submit"
                className="px-6 py-2.5 bg-gradient-to-r from-rose-500 to-rose-400 hover:from-rose-600 hover:to-rose-500 text-white rounded-lg shadow-md shadow-rose-500/20 transition-all font-medium disabled:opacity-70"
                disabled={isSubmitting || !feedback.trim()}
              >
                {isSubmitting ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Submitting...
                  </div>
                ) : (
                  'Submit Feedback'
                )}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default Feedback;
